<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitTitle="submitTitle"
    icon="fa-inventory"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />

    <div v-if="!loading && !loadingError">
      <FormGroup v-bind:form="dialogForm" :labelColumnSize="3">
        <FormRow label="Code Type">
          <FormOption label="Simple" name="codeStyle" :setting="InventoryBinCodeTypes.simple" v-model="inventoryBin.codeType" inline="true" />
          <FormOption label="Detailed" name="codeStyle" :setting="InventoryBinCodeTypes.detailed" v-model="inventoryBin.codeType" inline="true" />
        </FormRow>
        <div v-show="isCodeTypeSimple">
          <FormRow label="Code" helpTip="Main code to identify inventory bins. Should be unique">
            <FormInputText v-model="inventoryBin.code" field="code" placeholder="Bin Code" />
          </FormRow>
        </div>
        <div v-show="isCodeTypeDetailed">
          <FormRow label="Code">
            <FormInputText :value="inventoryBin.code" field="code" readonly note="Automatically generated by the details below" />
          </FormRow>
          <FormRow label="Zone">
            <FormInputText v-model="inventoryBin.zone" field="zone" placeholder="Bin Zone" />
          </FormRow>
          <FormRow label="Aisle">
            <FormInputText v-model="inventoryBin.aisle" field="aisle" placeholder="Bin Aisle" />
          </FormRow>
          <FormRow label="Bay">
            <FormInputText v-model="inventoryBin.bay" field="bay" placeholder="Bin Bay" />
          </FormRow>
          <FormRow label="Level">
            <FormInputText v-model="inventoryBin.level" field="level" placeholder="Bin Level" />
          </FormRow>
        </div>

        <FormHorizontalRule />
        <FormRow label="Description">
          <FormInputText v-model="inventoryBin.description" field="description" placeholder="Bin Description" />
        </FormRow>
        <FormRow label="Account Location">
          <AccountLocationSelect v-model="inventoryBin.accountLocationId" field="accountLocationId" />
        </FormRow>
        <FormRow label="Labels">
          <FormLabelTag popupName="Bin Labels" v-model="inventoryBin.labelList" labelType="inventoryBin" field="labelList" />
        </FormRow>        
      </FormGroup>
    </div>

  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'

import { InventoryBinCodeTypes } from '@/store/types'

export default {
  name: 'InventoryBinDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    AccountLocationSelect
  },
  props: {
    isCreateMode: Boolean,
    inventoryBinId: Number
  },
  data() {
    return {
      loading: false,
      loadingError: '',

      inventoryBin: {
        codeType: InventoryBinCodeTypes.simple,
        zone: '',
        aisle: '',
        bay: '',
        level: '',
        accountLocationId: null,
        labelList: []
      },
      dialogForm: {},
      submitting: false
    }
  },
  watch: {
    'inventoryBin.codeType': function() { this.generateCode() },
    'inventoryBin.zone': function() { this.generateCode() },
    'inventoryBin.aisle': function() { this.generateCode() },
    'inventoryBin.bay': function() { this.generateCode() },
    'inventoryBin.level': function() { this.generateCode() }
  },
  created() {
    this.InventoryBinCodeTypes = InventoryBinCodeTypes
  },
  mounted() {
  },
  computed: {
    title() {
      return this.isCreateMode ? 'New Inventory Bin' : 'Edit Inventory Bin'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    isCodeTypeSimple() {
      return this.inventoryBin.codeType == InventoryBinCodeTypes.simple
    },
    isCodeTypeDetailed() {
      return this.inventoryBin.codeType == InventoryBinCodeTypes.detailed
    }
  },
  methods: {
    show() {
      // Reset form
      this.inventoryBin = {
        codeType: InventoryBinCodeTypes.simple,
        zone: '',
        aisle: '',
        bay: '',
        level: '',
        accountLocationId: null,
        labelList: []
      }
      this.dialogForm = {}
      this.submitting = false

      if(!this.isCreateMode)
        this.requestData()

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      var invBinData = _.cloneDeep(this.$data.inventoryBin)
      invBinData.labelList = invBinData.labelList.map(label => label.id)

      if(this.isCreateMode) {
        this.$apiService.inventoryService.createInventoryBin(invBinData)
        .then(() => {
          this.submitting = false
          this.modalClosed(true)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.dialogForm = this.processFormResponse(err)
        })
      } else {
        this.$apiService.inventoryService.updateInventoryBin(invBinData.id, invBinData)
        .then(() => {
          this.submitting = false
          this.modalClosed(true)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.dialogForm = this.processFormResponse(err)
        })
      }
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      this.$apiService.inventoryService.getInventoryBin(this.inventoryBinId)
      .then(response => {
        this.inventoryBin = _.cloneDeep(response.data)

        this.inventoryBin.labelList = this.inventoryBin.labels
      })
      .catch(err => {
        console.error(err)
        this.loadingError = 'Error loading data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    generateCode() {
      var sectionCodeGen = (value, prefix) => {
        if(value == null)
          value = ''
        value = ('' + value).trim()
        if(!value)
          return ''

        // If a complete number, use prefix
        if(!isNaN(parseInt(value)))
          return `${prefix}${value}`

        return value
      }

      if(this.isCodeTypeDetailed) {
        this.inventoryBin.code = _.compact([
          sectionCodeGen(this.inventoryBin.zone, 'Z'),
          sectionCodeGen(this.inventoryBin.aisle, 'A'),
          sectionCodeGen(this.inventoryBin.bay, 'B'),
          sectionCodeGen(this.inventoryBin.level, 'L')
        ]).join('-')
      }
    }
  }
}
</script>